import { useEffect, useMemo, useRef } from 'react';
import type { ProfitAgentDrilldown, ProfitAgentQueryResponse } from '../../types/profitabilityAgent';
import { usePrefersReducedMotion, useTypewriter } from '../../hooks/useTypewriter';
import {
  maskProfitAgentDisplayText,
  maskProfitAgentDrilldownLabel,
  stripMarkdownForDisplay,
} from '../../utils/profitAgentText';

type ProfitAnswerCardProps = {
  result: ProfitAgentQueryResponse;
  /** When true, replace customer names and vehicle regs with demo placeholders. */
  maskEntities?: boolean;
  activeDrilldownId?: string | null;
  onDrilldown?: (d: ProfitAgentDrilldown) => void;
  onStreamingComplete?: () => void;
  /** Show the answer settled, without the typewriter (already revealed elsewhere). */
  disableStreaming?: boolean;
};

export default function ProfitAnswerCard({
  result,
  maskEntities = false,
  activeDrilldownId,
  onDrilldown,
  onStreamingComplete,
  disableStreaming = false,
}: ProfitAnswerCardProps) {
  const periodLabel =
    result.context?.date_from && result.context?.date_to
      ? `${result.context.date_from} – ${result.context.date_to}`
      : null;
  const periodPerspective = result.context?.period_perspective;
  const periodGrainLabel =
    periodPerspective?.customer?.short_label ||
    periodPerspective?.fleet?.short_label ||
    (periodPerspective?.window_days === 1 ? 'Single-day window' : null);
  const periodDetail =
    periodPerspective?.customer?.detail || periodPerspective?.fleet?.detail || null;

  const drilldowns = result.drilldowns ?? [];
  const maskOpts = useMemo(
    () => ({ mask: maskEntities, drilldowns }),
    [maskEntities, drilldowns],
  );

  const summary = useMemo(
    () => maskProfitAgentDisplayText(stripMarkdownForDisplay(result.summary), maskOpts),
    [result.summary, maskOpts],
  );
  const bullets = useMemo(
    () =>
      result.bullets
        .map((b) => maskProfitAgentDisplayText(stripMarkdownForDisplay(b), maskOpts))
        .filter(Boolean),
    [result.bullets, maskOpts],
  );
  const actions = useMemo(
    () =>
      result.recommended_actions
        .map((a) => maskProfitAgentDisplayText(stripMarkdownForDisplay(a), maskOpts))
        .filter(Boolean),
    [result.recommended_actions, maskOpts],
  );

  const prefersReducedMotion = usePrefersReducedMotion();
  const streamingEnabled = !prefersReducedMotion && !disableStreaming;
  const { displayText: typedSummary, isComplete: summaryComplete } = useTypewriter(summary, {
    charDelayMs: 14,
    enabled: streamingEnabled,
  });

  const answerKey = `${result.question}|${result.context?.date_from}|${result.context?.date_to}|${maskEntities}`;
  const notifiedForKeyRef = useRef<string | null>(null);
  useEffect(() => {
    notifiedForKeyRef.current = null;
  }, [answerKey]);
  useEffect(() => {
    if (!summaryComplete) return;
    if (notifiedForKeyRef.current === answerKey) return;
    notifiedForKeyRef.current = answerKey;
    onStreamingComplete?.();
  }, [summaryComplete, answerKey, onStreamingComplete]);

  const showDetails = summaryComplete || prefersReducedMotion;

  return (
    <article
      className="bg-card/40 rounded-xl border border-border/50 px-4 pt-3 pb-4 space-y-3 zoe-snap-in"
      aria-live="polite"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 text-base font-semibold text-foreground leading-snug">
          {typedSummary}
          {!summaryComplete && streamingEnabled ? <span className="zoe-stream-caret" aria-hidden /> : null}
        </div>
        {periodLabel ? (
          <div className="shrink-0 pt-0.5 text-right max-w-44">
            <p className="text-xs text-muted-foreground tabular-nums">{periodLabel}</p>
            {periodGrainLabel ? (
              <p className="text-xs text-primary/80 leading-tight mt-0.5">{periodGrainLabel}</p>
            ) : null}
          </div>
        ) : null}
      </div>

      {showDetails && periodDetail ? (
        <p className="text-xs text-muted-foreground leading-snug -mt-1">{periodDetail}</p>
      ) : null}

      {showDetails && bullets.length > 0 ? (
        <div className="mt-4 zoe-snap-in">
          <div className="text-sm font-medium text-muted-foreground mb-2">Key findings</div>
          <ul className="space-y-2 text-sm text-foreground list-disc list-inside marker:text-primary/80">
            {bullets.map((b, i) => (
              <li key={i} className="leading-relaxed">
                {b}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {showDetails && actions.length > 0 ? (
        <div className="mt-4 pt-4 border-t border-border/50 zoe-snap-in">
          <div className="text-sm font-medium text-muted-foreground mb-2">Recommended actions</div>
          <ul className="space-y-2 text-sm text-foreground list-disc list-inside marker:text-chart-10/70">
            {actions.map((a, i) => (
              <li key={i} className="leading-relaxed">
                {a}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {showDetails && drilldowns.length > 0 && onDrilldown ? (
        <div className="mt-4 pt-4 border-t border-border/50 zoe-snap-in">
          <div className="text-sm font-medium text-muted-foreground mb-2">View evidence</div>
          <div className="flex flex-wrap gap-2">
            {drilldowns.map((d) => {
              const key = `${d.type}|${d.id}`;
              const active = activeDrilldownId === key;
              const label = maskProfitAgentDrilldownLabel(d, maskEntities);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => onDrilldown(d)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
                    active
                      ? 'border-primary/60 bg-primary/20 text-foreground'
                      : 'border-accent bg-card/80 text-foreground hover:border-primary/50'
                  }`}
                >
                  <span className="capitalize">{d.type}</span>
                  <span className="font-semibold">{label}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </article>
  );
}
